import { apiRequest } from '../api';
import { state } from '../state';
import { navigateTo } from '../router';
import { t } from '../i18n';
import type { TranslationKey } from '../i18n/types';
import { attachWhoTooltip } from './admin-who';

// ============================================================
// Реакции на игру и голос за MVP.
// Один и тот же блок используется в модалке игры (полный вид)
// и в карточке списка «Игры» (компактный вид).
// Сводки кэшируются по id игры и грузятся пакетно: страница списка
// делает один запрос на все карточки, а не по запросу на игру.
// ============================================================

export type EmojiKind = 'fire' | 'clutch' | 'brain' | 'lucky' | 'throw' | 'boring';

/** Порядок и иконки реакций — используется и в картинке-итоге игры. */
export const EMOJI: Array<{ kind: EmojiKind; icon: string; key: TranslationKey }> = [
  { kind: 'fire', icon: '🔥', key: 'reactions.fire' },
  { kind: 'clutch', icon: '🎯', key: 'reactions.clutch' },
  { kind: 'brain', icon: '🧠', key: 'reactions.brain' },
  { kind: 'lucky', icon: '🍀', key: 'reactions.lucky' },
  { kind: 'throw', icon: '😂', key: 'reactions.throw' },
  { kind: 'boring', icon: '😴', key: 'reactions.boring' },
];

type MyReactions = Record<EmojiKind, boolean> & { mvp: number | null };

function emptyCounts(): Record<EmojiKind, number> {
  return Object.fromEntries(EMOJI.map((item) => [item.kind, 0])) as Record<EmojiKind, number>;
}

function emptyMy(): MyReactions {
  return {
    ...(Object.fromEntries(EMOJI.map((item) => [item.kind, false])) as Record<EmojiKind, boolean>),
    mvp: null,
  };
}

interface MvpEntry {
  player_id: number;
  player_name?: string;
  votes: number;
}

export interface ReactionsPayload {
  available: boolean;
  counts?: Record<EmojiKind, number>;
  mvp?: MvpEntry[];
  my?: MyReactions;
  total?: number;
}

interface GamePlayerLite {
  player_id: number;
  player_name: string;
}

export interface ReactionsBlockOptions {
  /** Компактный вид для карточки списка: без заголовка, MVP — за кнопкой. */
  compact?: boolean;
}

// --- Кэш сводок и пакетная загрузка -------------------------
const cache = new Map<number, ReactionsPayload>();
const listeners = new Map<number, Set<() => void>>();
const pendingIds = new Set<number>();
let flushTimer: number | null = null;
/** Размер пачки в одном запросе (совпадает с лимитом сервера). */
const BATCH_SIZE = 100;

function notify(gameId: number): void {
  for (const fn of listeners.get(gameId) ?? []) fn();
}

function scheduleLoad(gameId: number): void {
  pendingIds.add(gameId);
  if (flushTimer !== null) return;
  // Микрозадержка: карточки отрисовываются пачкой и уходят одним запросом.
  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    void flushPending();
  }, 0);
}

async function flushPending(): Promise<void> {
  const ids = [...pendingIds];
  pendingIds.clear();

  for (let i = 0; i < ids.length; i += BATCH_SIZE) {
    const chunk = ids.slice(i, i + BATCH_SIZE);
    try {
      const res = await apiRequest<{ items?: Record<string, ReactionsPayload> }>(
        `/api/games/reactions/summary?ids=${chunk.join(',')}`,
        { token: state.token },
      );
      const items = res.items ?? {};
      for (const id of chunk) {
        cache.set(id, items[String(id)] ?? { available: false });
      }
    } catch {
      // Сеть/сервер — помечаем недоступными, но не кэшируем ошибку навсегда
      // (следующая перерисовка снова попробует загрузить).
      for (const id of chunk) if (!cache.has(id)) cache.set(id, { available: false });
    }
    for (const id of chunk) notify(id);
  }
}

/** Сбросить кэш сводок (например, после перерисовки списка игр). */
export function resetReactionsCache(): void {
  cache.clear();
}

// Свои голоса зависят от пользователя — после входа/выхода кэш невалиден.
window.addEventListener('session:changed', () => cache.clear());

// --- Блок реакций ------------------------------------------
export function buildReactionsBlock(
  gameId: number,
  players: GamePlayerLite[],
  options: ReactionsBlockOptions = {},
): HTMLElement {
  const compact = options.compact === true;

  const root = document.createElement('section');
  root.className = 'reactions' + (compact ? ' reactions--card' : '');

  if (!compact) {
    const title = document.createElement('h4');
    title.className = 'reactions-title';
    title.textContent = t('reactions.title');
    root.appendChild(title);
  }

  const bar = document.createElement('div');
  bar.className = 'reaction-bar';
  root.appendChild(bar);

  const mvpBlock = document.createElement('div');
  mvpBlock.className = 'mvp-block';
  root.appendChild(mvpBlock);

  const note = document.createElement('p');
  note.className = 'reactions-note';
  root.appendChild(note);

  let payload: ReactionsPayload | null = cache.get(gameId) ?? null;
  let busy = false;
  let mvpOpen = false;
  /** Текст ошибки переживает перерисовку (сбрасывается при новой попытке). */
  let errorText = '';

  const goLogin = (): void => {
    sessionStorage.setItem(
      'redirectAfterLogin',
      window.location.pathname + window.location.search,
    );
    navigateTo('/auth');
  };

  const render = (): void => {
    payload = cache.get(gameId) ?? null;
    bar.innerHTML = '';
    mvpBlock.innerHTML = '';
    note.textContent = '';
    note.classList.remove('is-hint');

    if (payload && !payload.available) {
      // В карточке списка просто не показываем блок целиком.
      if (compact) root.classList.add('hidden');
      else note.textContent = t('reactions.unavailable');
      return;
    }
    if (compact) root.classList.remove('hidden');

    const loaded = payload !== null;
    const counts = payload?.counts ?? emptyCounts();
    const my = payload?.my ?? emptyMy();

    for (const item of EMOJI) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'reaction-btn' + (my[item.kind] ? ' is-active' : '');
      btn.disabled = busy || !loaded;
      btn.title = t(item.key);
      btn.setAttribute('aria-pressed', String(my[item.kind] === true));

      const emoji = document.createElement('span');
      emoji.className = 'reaction-emoji';
      emoji.textContent = item.icon;

      const count = document.createElement('span');
      count.className = 'reaction-count';
      count.textContent = loaded ? String(counts[item.kind] ?? 0) : '·';

      btn.append(emoji, count);
      btn.addEventListener('click', () => {
        if (!state.token) return goLogin();
        void send({ kind: item.kind });
      });
      attachWhoTooltip(btn, 'game', gameId, (who) => {
        const names = (who.reactions[item.kind] ?? []).map((p) => p.username).filter(Boolean);
        return names.length > 0 ? names.join(', ') : null;
      });
      bar.appendChild(btn);
    }

    // В карточке списка голосование за MVP прячем за кнопку: иначе лента
    // из 50 игр превращается в простыню.
    const mvpVotes = (payload?.mvp ?? []).reduce((sum, m) => sum + m.votes, 0);
    if (compact) {
      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className =
        'reaction-btn reaction-btn--mvp' +
        (my.mvp ? ' is-active' : '') +
        (mvpOpen ? ' is-open' : '');
      toggle.title = t('reactions.mvp');
      toggle.disabled = busy || !loaded;
      toggle.setAttribute('aria-expanded', String(mvpOpen));

      const mvpIcon = document.createElement('span');
      mvpIcon.className = 'reaction-emoji';
      mvpIcon.textContent = '⭐';

      const mvpLabel = document.createElement('span');
      mvpLabel.className = 'reaction-btn-label';
      mvpLabel.textContent = t('reactions.mvp_short');

      const mvpCount = document.createElement('span');
      mvpCount.className = 'reaction-count';
      mvpCount.textContent = loaded ? String(mvpVotes) : '·';

      toggle.append(mvpIcon, mvpLabel, mvpCount);
      toggle.addEventListener('click', () => {
        mvpOpen = !mvpOpen;
        render();
      });
      attachWhoTooltip(toggle, 'game', gameId, (who) => {
        const names = (who.reactions.mvp ?? []).map((p) => p.username).filter(Boolean);
        return names.length > 0 ? names.join(', ') : null;
      });
      bar.appendChild(toggle);
    }

    if (compact && !mvpOpen) {
      mvpBlock.classList.add('hidden');
    } else {
      mvpBlock.classList.remove('hidden');
      mvpBlock.append(buildMvpList());
    }

    const hasAnyVote = EMOJI.some((item) => my[item.kind]) || my.mvp !== null;
    if (compact && state.token && loaded && !hasAnyVote) {
      // Аккуратный намёк: игра ещё не оценена — пара кликов.
      note.classList.add('is-hint');
      note.textContent = t('reactions.todo');
    }

    if (!compact && !state.token) {
      const link = document.createElement('a');
      link.href = '#';
      link.className = 'hint-link';
      link.textContent = t('reactions.login');
      link.addEventListener('click', (e) => {
        e.preventDefault();
        goLogin();
      });
      note.appendChild(link);
    } else if (!compact) {
      note.textContent = t('reactions.mvp');
    }

    if (errorText) note.textContent = errorText;
  };

  const buildMvpList = (): HTMLElement | DocumentFragment => {
    const frag = document.createDocumentFragment();
    if (!payload) return frag;
    const my = payload.my ?? emptyMy();

    const hint = document.createElement('p');
    hint.className = 'mvp-hint';
    hint.textContent = t('reactions.mvp_hint');
    frag.appendChild(hint);

    const list = document.createElement('div');
    list.className = 'mvp-list';

    const votesById = new Map((payload.mvp ?? []).map((m) => [m.player_id, m.votes]));
    const maxVotes = Math.max(1, ...[...votesById.values()]);

    for (const player of players) {
      const votes = votesById.get(player.player_id) ?? 0;
      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'mvp-row' + (my.mvp === player.player_id ? ' is-mine' : '');
      // Лидер голосования — золотая подпись, чтобы результат читался сразу.
      if (votes > 0 && votes === maxVotes) row.classList.add('is-top');
      row.disabled = busy;
      row.setAttribute('aria-pressed', String(my.mvp === player.player_id));

      const barEl = document.createElement('span');
      barEl.className = 'mvp-bar';
      barEl.style.width = `${Math.round((votes / maxVotes) * 100)}%`;

      const name = document.createElement('span');
      name.className = 'mvp-name';
      name.textContent = player.player_name;

      const votesEl = document.createElement('span');
      votesEl.className = 'mvp-votes';
      votesEl.textContent = votes > 0 ? t('reactions.votes', { n: votes }) : '';

      row.append(barEl, name, votesEl);
      row.addEventListener('click', () => {
        if (!state.token) return goLogin();
        const next = my.mvp === player.player_id ? null : player.player_id;
        void send({ kind: 'mvp', player_id: next });
      });
      attachWhoTooltip(row, 'game', gameId, (who) => {
        const names = (who.reactions.mvp ?? [])
          .filter((p) => p.player_name === player.player_name)
          .map((p) => p.username)
          .filter(Boolean);
        return names.length > 0 ? names.join(', ') : null;
      });
      list.appendChild(row);
    }

    frag.appendChild(list);
    return frag;
  };

  const send = async (body: { kind: string; player_id?: number | null }): Promise<void> => {
    if (busy) return;
    busy = true;
    errorText = '';
    render();
    try {
      const next = await apiRequest<ReactionsPayload>(`/api/games/${gameId}/reactions`, {
        method: 'POST',
        token: state.token,
        body,
      });
      cache.set(gameId, next);
    } catch (err) {
      console.warn('[reactions] save failed:', err);
      errorText = t('reactions.error');
      if (compact) mvpOpen = false;
    } finally {
      busy = false;
      render();
      notify(gameId);
    }
  };

  // Подписка на обновления сводки (пакетная загрузка / голос из другой карточки).
  const listener = (): void => {
    // Блок уже выброшен из DOM (перерисовали список) — отписываемся.
    if (!root.isConnected) {
      listeners.get(gameId)?.delete(listener);
      return;
    }
    if (busy) return;
    render();
  };
  const set = listeners.get(gameId) ?? new Set<() => void>();
  listeners.set(gameId, set);
  set.add(listener);

  if (!cache.has(gameId)) scheduleLoad(gameId);
  render();
  return root;
}
